import {
    Badge,
    Button,
    Container,
    HStack,
    IconButton,
    SimpleGrid,
    Spinner,
    Text,
    VStack,
    useColorModeValue,
    useToast,
} from "@chakra-ui/react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import CatalogItemCard from "../components/CatalogItemCard";
import {Icons} from "../components/Icons";
import {useCatalogItemStore} from "../store/catalogItem";
import {useShallow} from "zustand/react/shallow";

type ViewMode = "grid" | "list";

const HomePage = () => {
    const toast = useToast();

    const {items, isLoading, error, fetchCatalogItems} = useCatalogItemStore(
        useShallow((s) => ({
            items: s.items,
            isLoading: s.isLoading,
            error: s.error,
            fetchCatalogItems: s.fetchCatalogItems,
        })),
    );

    const [viewMode, setViewMode] = useState<ViewMode>("grid");

    const showLoadError = useCallback(
        (message: string): void => {
            toast({
                title: "Failed to load catalog",
                description: message,
                status: "error",
                isClosable: true,
            });
        },
        [toast],
    );

    useEffect(() => {
        void (async () => {
            const r = await fetchCatalogItems();
            if (!r.success) showLoadError(r.message);
        })();
    }, [fetchCatalogItems, showLoadError]);

    const handleRefresh = async (): Promise<void> => {
        const r = await fetchCatalogItems();
        if (!r.success) {
            toast({
                title: "Refresh failed",
                description: r.message,
                status: "error",
                isClosable: true,
            });
        }
    };

    const muted = useColorModeValue("gray.600", "whiteAlpha.700");

    const viewBadge = useMemo(() => {
        return viewMode === "grid" ? (
            <Badge variant="subtle">Grid</Badge>
        ) : (
            <Badge variant="subtle">List</Badge>
        );
    }, [viewMode]);

    return (
        <Container maxW="1140px" py={{base: 6, md: 10}}>
            <VStack spacing={6} align="stretch">
                <HStack justify="space-between" flexWrap="wrap" gap={2}>
                    <HStack spacing={3}>
                        <Text fontSize={{base: "xl", md: "2xl"}} fontWeight="semibold">
                            Catalog
                        </Text>
                        {viewBadge}
                    </HStack>

                    <HStack spacing={2}>
                        <IconButton
                            aria-label="Grid view"
                            icon={<Icons.Grid size={18} />}
                            variant={viewMode === "grid" ? "solid" : "ghost"}
                            onClick={() => setViewMode("grid")}
                            isDisabled={isLoading}
                            size="sm"
                        />
                        <IconButton
                            aria-label="List view"
                            icon={<Icons.List size={18} />}
                            variant={viewMode === "list" ? "solid" : "ghost"}
                            onClick={() => setViewMode("list")}
                            isDisabled={isLoading}
                            size="sm"
                        />

                        <Button
                            onClick={() => void handleRefresh()}
                            isLoading={isLoading}
                            variant="outline"
                            size="sm"
                        >
                            <HStack spacing={1} alignItems="center" wrap="nowrap">
                                <Icons.Refresh size={16} />
                                <Text>Refresh</Text>
                            </HStack>
                        </Button>

                        <Link to="/create">
                            <Button leftIcon={<Icons.Add size={16} />} size="sm">
                                Add
                            </Button>
                        </Link>
                    </HStack>
                </HStack>

                {error ? (
                    <Text color="red.400">{error}</Text>
                ) : isLoading && items.length === 0 ? (
                    <HStack justify="center" py={10}>
                        <Spinner />
                        <Text color={muted}>Loading…</Text>
                    </HStack>
                ) : items.length === 0 ? (
                    <Text color={muted}>No items yet.</Text>
                ) : viewMode === "grid" ? (
                    <SimpleGrid columns={{base: 1, sm: 2, md: 3}} spacing={4}>
                        {items.map((item) => (
                            <CatalogItemCard key={item._id} item={item} layout={viewMode} />
                        ))}
                    </SimpleGrid>
                ) : (
                    <VStack align="stretch" spacing={3}>
                        {items.map((item) => (
                            <CatalogItemCard key={item._id} item={item} layout={viewMode} />
                        ))}
                    </VStack>
                )}
            </VStack>
        </Container>
    );
};

export default HomePage;